import { BotbLeaderboardContext } from "./context";
import React, { useEffect, useState } from "react";
import { getActiveCompetitions } from "./service";
import useSmartico from "../../hooks/useSmartico";
import { SmarticoTournament, SmarticoTournamentPlayer, SmarticoTournamentPrize } from "../../interfaces/smartico";
import { useAppDispatch } from "../../redux/hooks";
import { publishModalData, publishModalProps, publishModalVisibility } from "../../redux/features/modal/modalSlice";
import LeaderboardModal from "./components/LeaderboardModal";
import { getUserSubscriptionPlan } from "../subscription/subscription-service";

const TOURNAMENT_ID = 5630;

const BotbLeaderboardProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [youPlayer, setPlayer] = useState<SmarticoTournamentPlayer | null>(null);
  const [leaderboardPlayers, setLeaderboardPlayers] = useState<SmarticoTournamentPlayer[]>([]);
  const [isLeaderboardDataLoading, setIsLeaderboardDataLoading] = useState(true);
  const [prizesData, setPrizesData] = useState<SmarticoTournamentPrize[]>([]);
  const [activeCompetitionsLoading, setActiveCompetitionsLoading] = useState(true);
  const [activeCompetitions, setActiveCompetitions] = useState(false);
  const [tournamentEndDate, setTournamentEndDate] = useState(0);
  const [hasSubscription, setHasSubscription] = useState(false);
  const tournamentFinished = isLeaderboardDataLoading ? false : Date.now() >= tournamentEndDate;
  const api = useSmartico();
  const dispatch = useAppDispatch();

  const openPrizesModal = () => {
    dispatch(
      publishModalData({
        modalData: <LeaderboardModal />,
      })
    );
    dispatch(
      publishModalProps({
        withOverlay: false,
      })
    );
    dispatch(publishModalVisibility({ isOpen: true }));
  };

  const closePrizesModal = () => {
    dispatch(publishModalVisibility({ isOpen: false }));
  };

  useEffect(() => {
    (async () => {
      try {
        const response = await getUserSubscriptionPlan();
        const subscription = (await response.json()) as SubscriptionPlanUser;
        setHasSubscription(typeof subscription !== "undefined");
      } catch (err) {
        setHasSubscription(false);
      }
    })();
  }, []);

  useEffect(() => {
    getActiveCompetitions()
      .then(res => res.json())
      .then(res => {
        setActiveCompetitionsLoading(false);
        const resCompetitions = getFinalData(res);
        setActiveCompetitions(resCompetitions);
      })
      .catch(err => console.error(err));

    api()
      .then(async res => {
        try {
          const tournamentsList = (await res.getTournamentsList()) as SmarticoTournament[];
          const chosenTournament = tournamentsList.find(tournament => tournament.tournament_id === TOURNAMENT_ID);
          if (chosenTournament) {
            const tournamentData = await res.getTournamentInstanceInfo(chosenTournament.instance_id);
            const players = tournamentData.players as SmarticoTournamentPlayer[];
            setTournamentEndDate(tournamentData.end_time);
            setLeaderboardPlayers(players);
            setPrizesData(tournamentData.prizes as SmarticoTournamentPrize[]);
            setIsLeaderboardDataLoading(false);
            setPlayer(tournamentData.me || null);
          }
        } catch (err) {
          console.error(err);
        }
      })
      .catch(err => console.error(err));
  }, []);

  const zeroPrices = ["0.00", "0,00"];

  const getReferencePriority = (reference: any) => {
    if (reference.includes("HC")) return 2;
    if (reference.includes("DC")) return 1;
    if (reference.includes("IW") || reference.includes("IA") || reference.includes("IB") || reference.includes("IC"))
      return 4;
    if (reference.includes("XC")) return 3;
    if (reference.includes("LS")) return 5;
    if (reference.includes("EW")) return 6;
    return 6;
  };

  const getHighestInstantWin = (data: any) => {
    const instantWins = data.filter(
      (competition: any) =>
        competition.Reference.includes("IW") ||
        competition.Reference.includes("IA") ||
        competition.Reference.includes("IB") ||
        competition.Reference.includes("IC")
    );
    if (instantWins.length === 0) return data;
    instantWins.sort(
      (a: any, b: any) => parseFloat(b.FormattedPriceWithoutCurrency) - parseFloat(a.FormattedPriceWithoutCurrency)
    );
    const highestInstantWin = instantWins[0];
    const otherCompetitions = data.filter(
      (competition: any) =>
        !competition.Reference.includes("IW") &&
        !competition.Reference.includes("IA") &&
        !competition.Reference.includes("IB") &&
        !competition.Reference.includes("IC")
    );
    return [...otherCompetitions, highestInstantWin];
  };

  const parseCompetitionData = (data: any) => {
    const hasPriceCompetitions = data.filter(
      (competition: any) => !zeroPrices.includes(competition.FormattedPriceWithoutCurrency)
    );
    return getHighestInstantWin(hasPriceCompetitions)
      .sort((a: any, b: any) => getReferencePriority(a.Reference) - getReferencePriority(b.Reference))
      .map((competition: any) => {
        if (competition.Reference.includes("DC")) {
          return {
            ...competition,
            ticketCount: 1,
            FormattedOriginalPrice: "4.10",
          };
        } else if (competition.Reference.includes("HC")) {
          return {
            ...competition,
            ticketCount: 40,
            FormattedOriginalPrice: competition.FormattedOriginalPrice.replace(competition.CurrencySymbol, ""),
          };
        } else if (competition.Reference.includes("LS")) {
          return {
            ...competition,
            ticketCount: 15,
            FormattedOriginalPrice: "2.85",
          };
        }
        return {
          ...competition,
          ticketCount: 15,
          FormattedOriginalPrice: competition.FormattedOriginalPrice.replace(competition.CurrencySymbol, ""),
        };
      });
  };

  const getFinalData = (data: any) => {
    return data?.length > 0 ? parseCompetitionData(data) : [];
  };

  const getPrize = (tournamentPlayer: SmarticoTournamentPlayer) => {
    if (!tournamentPlayer) {
      return null;
    }
    return prizesData.find(
      prize => tournamentPlayer.position >= prize.place_from && tournamentPlayer.position <= prize.place_to
    );
  };

  return (
    <BotbLeaderboardContext.Provider
      value={{
        activeCompetitionsLoading,
        activeCompetitions,
        leaderboardPlayers,
        prizesData,
        isLeaderboardDataLoading,
        getPrize,
        openPrizesModal,
        closePrizesModal,
        activeTab,
        setActiveTab,
        tournamentEndDate,
        hasSubscription,
        youPlayer,
        tournamentFinished,
      }}>
      {children}
    </BotbLeaderboardContext.Provider>
  );
};

export default BotbLeaderboardProvider;
